/**
 * AGI Intelligence View Components
 * EXACT copy from IntelligenceStudioPage.jsx lines 2275-2701
 */

import React, { useState, useMemo } from 'react';

import {
  Layers,
  Loader2,
  Target,
  Clock,
  Users,
  Network,
  Sparkles,
  RefreshCw,
  AlertCircle,
  Zap,
} from 'lucide-react';

// ==========================================
// AGI INTELLIGENCE VIEW COMPONENTS
// ==========================================

// Intelligence View Content - Full AGI Analysis Dashboard
const IntelligenceViewContent = ({ data, isLoading, onAnalyze, notebook }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        <span className="mt-3 text-sm text-slate-500">Running AGI intelligence analysis...</span>
        <span className="mt-1 text-xs text-slate-400">This may take 30-60 seconds</span>
      </div>
    );
  }

  if (!data || !data.success) {
    return (
      <div className="p-6 text-center">
        <Layers className="h-12 w-12 mx-auto text-amber-300 dark:text-amber-700 mb-3" />
        <p className="text-sm font-medium text-slate-600 dark:text-neutral-300 mb-2">
          AGI Intelligence Engine
        </p>
        <p className="text-xs text-slate-400 dark:text-neutral-500 mb-4 max-w-[240px] mx-auto">
          Run deep analysis to extract cross-domain insights, action items, and strategic scenarios.
        </p>
        <button
          onClick={onAnalyze}
          className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-500 text-white text-sm rounded-lg hover:from-amber-700 hover:to-orange-600 shadow-lg"
        >
          🧠 Run Full Analysis
        </button>
        <p className="mt-3 text-[10px] text-slate-400">
          Analyzes Legal • FMCG • Banking • Strategy
        </p>
      </div>
    );
  }

  const domain = data.metadata?.primary_domain || 'general';
  const confidence = Math.round((data.metadata?.confidence || 0) * 100);
  // ✅ FIX: Get extracted domains to filter irrelevant content
  const extractedDomains = data.metadata?.extracted_domains || [domain];

  // Domain colors
  const domainColors = {
    legal: 'from-amber-500 to-orange-500',
    fmcg: 'from-green-500 to-green-600',
    banking: 'from-blue-500 to-cyan-500',
    finance: 'from-yellow-500 to-orange-500',
    general: 'from-slate-500 to-gray-500',
  };

  // ✅ FIX: Count actions only for extracted domains
  const actionCount =
    (extractedDomains.includes('legal') ? data.action_items?.legal_actions?.length || 0 : 0) +
    (extractedDomains.includes('fmcg') ? data.action_items?.fmcg_actions?.length || 0 : 0) +
    (extractedDomains.includes('banking') ? data.action_items?.banking_actions?.length || 0 : 0);

  return (
    <div className="p-4 overflow-y-auto h-full space-y-4">
      {/* Header with domain badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-lg bg-gradient-to-br ${domainColors[domain] || domainColors.general} flex items-center justify-center`}
          >
            <Layers className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-neutral-200">
              {domain.charAt(0).toUpperCase() + domain.slice(1)} Document
            </p>
            <p className="text-xs text-slate-400">{confidence}% confidence</p>
          </div>
        </div>
        <button
          onClick={onAnalyze}
          className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded hover:bg-amber-200"
        >
          🔄 Re-analyze
        </button>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-amber-600">
            {data.cross_domain?.cross_domain_links?.length || 0}
          </p>
          <p className="text-[10px] text-amber-500">Links</p>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-orange-600">{actionCount}</p>
          <p className="text-[10px] text-orange-500">Actions</p>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2 text-center">
          <p className="text-lg font-bold text-red-600">{data.scenarios?.length || 0}</p>
          <p className="text-[10px] text-red-500">Scenarios</p>
        </div>
      </div>

      {/* Top Strategic Insights - only if multiple domains extracted */}
      {extractedDomains.length >= 2 && data.cross_domain?.strategic_insights?.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-3">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">
            💡 Cross-Domain Insights
          </p>
          <div className="space-y-2">
            {data.cross_domain.strategic_insights.slice(0, 3).map((insight, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-amber-600 text-sm">•</span>
                <p className="text-xs text-slate-700 dark:text-neutral-300">{insight.insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain-specific insights for single domain */}
      {extractedDomains.length === 1 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-3">
          <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">
            💡 {domain.charAt(0).toUpperCase() + domain.slice(1)} Insights
          </p>
          <p className="text-xs text-slate-600 dark:text-neutral-400">
            This is a single-domain {domain} document. Cross-domain reasoning is not applicable.
          </p>
        </div>
      )}

      {/* Agency/Actors Summary */}
      {data.universal?.agency?.primary_actors?.length > 0 && (
        <div className="bg-white dark:bg-neutral-700/50 rounded-lg p-3 border border-slate-200 dark:border-neutral-600">
          <p className="text-xs font-medium text-slate-600 dark:text-neutral-400 mb-2">
            👥 Key Actors
          </p>
          <div className="flex flex-wrap gap-1">
            {data.universal.agency.primary_actors.slice(0, 6).map((actor, idx) => (
              <span
                key={idx}
                className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full"
              >
                {actor.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick Win Actions */}
      {data.action_items?.quick_wins?.length > 0 && (
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
          <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">
            ⚡ Quick Wins
          </p>
          <div className="space-y-1">
            {data.action_items.quick_wins.slice(0, 3).map((item, idx) => (
              <div key={idx} className="flex items-start gap-2">
                <span className="text-green-500">✓</span>
                <p className="text-xs text-green-800 dark:text-green-300">{item.action}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Actions View Content - Prioritized Action Items
const ActionsViewContent = ({ data, isLoading, onRefresh, notebook }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
        <span className="mt-2 text-sm text-slate-500">Loading action items...</span>
      </div>
    );
  }

  const actions = data?.actions || [];
  const quickWins = data?.quick_wins || [];
  const watchItems = data?.watch_items || [];

  if (!actions.length && !quickWins.length) {
    return (
      <div className="p-6 text-center">
        <Target className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-3">No action items yet</p>
        <p className="text-xs text-slate-400 mb-4">
          Run intelligence analysis first to generate actions.
        </p>
      </div>
    );
  }

  const priorityColors = {
    critical:
      'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
    high: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',
    medium:
      'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800',
    low: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
  };

  const domainIcons = {
    legal: '⚖️',
    fmcg: '🛒',
    banking: '🏦',
    strategic: '🎯',
  };

  return (
    <div className="p-4 overflow-y-auto h-full space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-700 dark:text-neutral-300">
          📋 Action Items ({actions.length})
        </p>
        <button onClick={onRefresh} className="text-xs text-amber-600 hover:underline">
          🔄 Refresh
        </button>
      </div>

      {/* Actions List */}
      <div className="space-y-3">
        {actions.slice(0, 10).map((action, idx) => (
          <div
            key={idx}
            className={`rounded-lg p-3 border ${priorityColors[action.priority] || priorityColors.medium}`}
          >
            <div className="flex items-start justify-between mb-1">
              <span className="text-lg">{domainIcons[action.domain] || '📌'}</span>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-medium ${
                  action.priority === 'critical'
                    ? 'bg-red-200 text-red-800'
                    : action.priority === 'high'
                      ? 'bg-orange-200 text-orange-800'
                      : 'bg-yellow-200 text-yellow-800'
                }`}
              >
                {action.priority}
              </span>
            </div>
            <p className="text-sm font-medium mb-1">{action.action}</p>
            {action.rationale && <p className="text-xs opacity-80">{action.rationale}</p>}
            {action.deadline && (
              <p className="text-xs mt-1 flex items-center gap-1">
                <Clock className="h-3 w-3" /> {action.deadline}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Watch Items */}
      {watchItems.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-neutral-600">
          <p className="text-xs font-medium text-slate-600 dark:text-neutral-400 mb-2">
            👁️ Watch Items
          </p>
          <div className="space-y-2">
            {watchItems.slice(0, 3).map((item, idx) => (
              <div key={idx} className="text-xs bg-slate-100 dark:bg-neutral-700 rounded p-2">
                <p className="font-medium">{item.item}</p>
                <p className="text-slate-500 dark:text-neutral-400">Trigger: {item.trigger}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Cross-Domain View Content - Links and Impact Chains
const CrossDomainViewContent = ({ data, isLoading, onRefresh, notebook }) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
        <span className="mt-2 text-sm text-slate-500">Loading cross-domain insights...</span>
      </div>
    );
  }

  const links = data?.cross_domain_links || [];
  const chains = data?.impact_chains || [];
  const insights = data?.strategic_insights || [];

  if (!links.length && !chains.length) {
    return (
      <div className="p-6 text-center">
        <Network className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
        <p className="text-sm text-slate-500 dark:text-neutral-400 mb-3">
          No cross-domain links found
        </p>
        <p className="text-xs text-slate-400 mb-4">
          Run intelligence analysis to discover connections.
        </p>
      </div>
    );
  }

  const domainColors = {
    legal: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    fmcg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    banking: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    operations: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  };

  return (
    <div className="p-4 overflow-y-auto h-full space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-700 dark:text-neutral-300">
          🔗 Cross-Domain Intelligence
        </p>
        <button onClick={onRefresh} className="text-xs text-amber-600 hover:underline">
          🔄 Refresh
        </button>
      </div>

      {/* Cross-Domain Links */}
      {links.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-600 dark:text-neutral-400">
            Domain Connections
          </p>
          {links.slice(0, 5).map((link, idx) => (
            <div
              key={idx}
              className="bg-white dark:bg-neutral-700/50 rounded-lg p-3 border border-slate-200 dark:border-neutral-600"
            >
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`text-xs px-2 py-0.5 rounded ${domainColors[link.from_domain] || 'bg-slate-100'}`}
                >
                  {link.from_domain}
                </span>
                <span className="text-slate-400">→</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${domainColors[link.to_domain] || 'bg-slate-100'}`}
                >
                  {link.to_domain}
                </span>
                <span
                  className={`ml-auto text-[10px] ${
                    link.strength === 'high'
                      ? 'text-red-500'
                      : link.strength === 'medium'
                        ? 'text-yellow-500'
                        : 'text-green-500'
                  }`}
                >
                  {link.strength}
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-neutral-300">{link.reasoning}</p>
            </div>
          ))}
        </div>
      )}

      {/* Impact Chains */}
      {chains.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-neutral-600">
          <p className="text-xs font-medium text-slate-600 dark:text-neutral-400 mb-2">
            ⛓️ Impact Chains
          </p>
          {chains.slice(0, 3).map((chain, idx) => (
            <div
              key={idx}
              className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-neutral-800 dark:to-neutral-700 rounded-lg p-3 mb-2"
            >
              <p className="text-xs font-medium text-slate-700 dark:text-neutral-200 mb-2">
                {chain.name}
              </p>
              <div className="space-y-1">
                {(chain.steps || []).map((step, stepIdx) => (
                  <div key={stepIdx} className="flex items-center gap-2 text-xs">
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">
                      {step.step}
                    </span>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] ${domainColors[step.domain] || 'bg-slate-100'}`}
                    >
                      {step.domain}
                    </span>
                    <span className="text-slate-600 dark:text-neutral-300 flex-1">
                      {step.event}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Strategic Insights */}
      {insights.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-neutral-600">
          <p className="text-xs font-medium text-slate-600 dark:text-neutral-400 mb-2">
            💡 Strategic Insights
          </p>
          <div className="space-y-2">
            {insights.slice(0, 4).map((insight, idx) => (
              <div
                key={idx}
                className="bg-amber-50 dark:bg-amber-900/20 rounded p-2 border-l-2 border-amber-500"
              >
                <p className="text-xs text-amber-800 dark:text-amber-400">{insight.insight}</p>
                {insight.action && (
                  <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                    ➜ {insight.action}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { IntelligenceViewContent, ActionsViewContent, CrossDomainViewContent };
